import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  ChangeLotQualityStatusDocument,
  type ChangeLotQualityStatusMutation,
  type InventoryChangeQualityStatusFailure,
  type InventoryChangeQualityStatusInput,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

// Workaround: The backend gives every quality status failure the same title and no error code, so the lot-not-found
// failure is recognized by its message. Remove once the backend returns a distinguishing error code.
const LOT_NOT_FOUND_ERROR_MESSAGE_PREFIX = "Lot does not exist in the Product Lot Master"

export enum ChangeLotQualityStatusResult {
  Success = "success",
  Failed = "failed",
  LotNotFound = "lot-not-found",
}

type ChangeLotQualityStatusOutcome =
  | { result: ChangeLotQualityStatusResult.Success | ChangeLotQualityStatusResult.Failed }
  | { result: ChangeLotQualityStatusResult.LotNotFound; message: string }

interface UseChangeLotQualityStatusReturn {
  changeLotQualityStatus: (input: InventoryChangeQualityStatusInput) => Promise<ChangeLotQualityStatusOutcome>
}

export const useChangeLotQualityStatus = (): UseChangeLotQualityStatusReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [, executeMutation] = useMutation<ChangeLotQualityStatusMutation, { input: InventoryChangeQualityStatusInput }>(
    ChangeLotQualityStatusDocument
  )

  const changeLotQualityStatus = useCallback(
    async (input: InventoryChangeQualityStatusInput): Promise<ChangeLotQualityStatusOutcome> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.inventory.changeQualityStatus
      const hasError = error || output?.__typename === "InventoryChangeQualityStatusFailure"

      if (hasError) {
        const message = error?.message ?? (output as InventoryChangeQualityStatusFailure).error.message

        // The lots tab shows this failure in its own dialog instead of a notification.
        if (message.startsWith(LOT_NOT_FOUND_ERROR_MESSAGE_PREFIX)) {
          return { result: ChangeLotQualityStatusResult.LotNotFound, message }
        }

        notify({ type: NotificationType.Error, message })

        return { result: ChangeLotQualityStatusResult.Failed }
      }

      notify({ type: NotificationType.Success, message: "Quality status change sent to AS400" })

      return { result: ChangeLotQualityStatusResult.Success }
    },
    [executeMutation, notify]
  )

  return { changeLotQualityStatus }
}
